import { useEffect, HTMLAttributes } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Modal, { ClosableModalWrapperComponentProps } from "../modal";
import Feedback from "../../feedback/feedback-container";
import { User } from "../../../model/User";
import { FeedbackEntry } from "../../feedback";
import {
  ClearErrorAction,
  GetUserAction,
  isAddErrorAction,
  RemoveUserFromOrgRoleAction,
} from "../../../actions/actionTypes";
import UserUtils from "../../../utils/user";
import { UserValues } from "../../../localization/user";

//<editor-fold desc="Props">

export interface UserWithAdminStatus extends User {
  isOrgAdmin: boolean;
}

const resolveUserName = (
  user: User | undefined | null,
  intl: { formatMessage: (v: any, v2?: any) => string }
) =>
  UserUtils.resolveDisplayName(
    user,
    intl.formatMessage(UserValues.displayName.undefined)
  );
export interface UnassignAdminModalDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface UnassignAdminModalDataProps
  extends Pick<ClosableModalWrapperComponentProps, "isReady"> {
  onUnassignAdmin: (
    roleId: string,
    userId: string
  ) => Promise<RemoveUserFromOrgRoleAction>;
  onLoadUser: (id: string) => Promise<GetUserAction>;
  user?: UserWithAdminStatus | null;
  onLoadOrgadminRole: () => void;
  orgAdminRoleId?: string;
  actorId: string;
  onClearError: (errorId: string) => ClearErrorAction;
}
export interface UnassignAdminModalProps
  extends UnassignAdminModalDOMProps,
    UnassignAdminModalDataProps,
    Omit<ClosableModalWrapperComponentProps, "isReady"> {
  onShowFeedback: (feedback: FeedbackEntry) => void;
  userId?: string;
}
//</editor-fold>

function UnassignAdminModal(props: UnassignAdminModalProps) {
  //<editor-fold desc="Local variables">
  let {
    userId,
    user,
    actorId,
    show,
    onClose,
    onLoadOrgadminRole,
    orgAdminRoleId,
    onUnassignAdmin,
    onLoadUser,
    onShowFeedback,
    isReady,
    onExited,
    onClearError,
  } = props;

  // this is more like a variable than a hook
  const intl = useIntl();

  const userObjId = user ? user.id : undefined;
  useEffect(() => {
    if (
      !!show &&
      !!userId &&
      (userObjId === undefined || (!!userObjId && userObjId !== userId)) &&
      !!onLoadUser
    ) {
      onLoadUser(userId).then((res) => {
        if (!userObjId && isAddErrorAction(res)) {
          // only clear error if no data exists, leave if previous data is still available and
          // let the api error notification handler show error
          onClearError(res.error?.errorId);
        }
      });
    }
  }, [show, userId, userObjId, onLoadUser, onClearError]);
  useEffect(() => {
    if (show && orgAdminRoleId === undefined && onLoadOrgadminRole) {
      onLoadOrgadminRole();
    }
  }, [show, orgAdminRoleId, onLoadOrgadminRole]);
  //</editor-fold>

  return (
    <Modal
      onReloadData={() => {
        if ((user || userId) && onLoadUser) {
          onLoadUser(user ? (user.id as string) : (userId as string)).then(
            (res) => {
              if (!user && isAddErrorAction(res)) {
                // only clear error if no data exists, leave if previous data is still available and
                // let the api error notification handler show error
                onClearError(res.error?.errorId);
              }
            }
          );
        }
        if (onLoadOrgadminRole) {
          onLoadOrgadminRole();
        }
      }}
      onExited={onExited}
      isReady={isReady}
      data-test-unassign-admin-modal={user ? user.id : true}
      title={
        !isReady || user
          ? intl.formatMessage({
              defaultMessage: "Unassign Admin access",
              description: "modal heading",
            })
          : intl.formatMessage({
              defaultMessage: "User not found",
              description: "modal heading when user not found",
            })
      }
      show={show}
      onClose={onClose}
      primaryButton={
        user && user.isOrgAdmin
          ? {
              label: intl.formatMessage({
                defaultMessage: "Unassign",
                description: "primary button label",
              }),
            }
          : {
              label: intl.formatMessage({
                defaultMessage: "Close",
                description: "close button label",
              }),
            }
      }
      onPrimaryAction={() => {
        if (show && user && user.isOrgAdmin && orgAdminRoleId) {
          onUnassignAdmin(orgAdminRoleId, user.id as string).then((res) => {
            if (!isAddErrorAction(res)) {
              onShowFeedback({
                id: "unassignAdmin_" + (user ? (user.id as string) : ""),
                msg: intl.formatMessage(
                  {
                    defaultMessage: "Admin access unassigned for {name}.",
                    description:
                      "Success notification. 'name' = name of the user",
                  },
                  {
                    name:
                      "<strong>" + resolveUserName(user, intl) + "</strong>",
                  }
                ),
                autoclose: true,
                type: "success",
              });
            } else {
              onClearError(res.error?.errorId);
              onShowFeedback({
                id: "unassignAdmin_" + (user ? (user.id as string) : ""),
                msg: intl.formatMessage(
                  {
                    defaultMessage:
                      "Unassigning Admin access for {name} failed.",
                    description:
                      "Failure notification. 'name' = name of the user",
                  },
                  {
                    name:
                      "<strong>" + resolveUserName(user, intl) + "</strong>",
                  }
                ),
                type: "danger",
              });
            }
          });
        }
        onClose();
      }}
      secondaryButton={
        user && user.isOrgAdmin
          ? {
              label: intl.formatMessage({
                defaultMessage: "Cancel",
                description: "secondary button label",
              }),
            }
          : undefined
      }
      onSecondaryAction={user ? onClose : undefined}
    >
      <>
        {show && user && (
          <>
            {user.id === actorId && (
              <Feedback type={"danger"} show={true} asChild={true}>
                <p>
                  <FormattedMessage
                    defaultMessage="{name}, you're not allowed to unassign yourself."
                    description="Info copy for disabled self removal. User is not allowed to remove herself from the admin role because that would cause losing access to the application. 'name' = name of the user"
                    values={{
                      name: <strong>{resolveUserName(user, intl)}</strong>,
                    }}
                  />
                </p>
              </Feedback>
            )}
            {!user.isOrgAdmin && user.id !== actorId && (
              <Feedback type={"danger"} show={true} asChild={true}>
                <p>
                  <FormattedMessage
                    defaultMessage="{name} does not have Admin access to unassign."
                    description="message to be shown when the user is not an admin. 'name' = name of the user"
                    values={{
                      name: <strong>{resolveUserName(user, intl)}</strong>,
                    }}
                  />
                </p>
              </Feedback>
            )}
            {user.isOrgAdmin && user.id !== actorId && (
              <p>
                <FormattedMessage
                  defaultMessage="Are you sure you wish to unassign Admin access for {name}?"
                  description={
                    "confirm action message. 'name' = name of the user"
                  }
                  values={{
                    name: <strong>{resolveUserName(user, intl)}</strong>,
                  }}
                />
              </p>
            )}
          </>
        )}
        {isReady && show && !user && (
          <Feedback type={"danger"} show={true} asChild={true}>
            <p>
              <FormattedMessage
                defaultMessage="Something went wrong and the user could not be loaded. The user may have been removed or you don't have sufficient access rights."
                description="message to be shown when there is no user to display"
              />
            </p>
          </Feedback>
        )}
      </>
    </Modal>
  );
}

export default UnassignAdminModal;
